import { Button } from "@/components/button";
import { Combobox } from "@/components/combobox";
import CalendarDateRangePicker from "@/components/data-range-picker";
import { Label } from "@/components/label";
import { LoadingSpinner } from "@/components/loading-spinner";
import QuestionMarkTooltip from "@/components/questionmark-tooltip";
import { Separator } from "@/components/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { CalendarDates } from "@/custom-types";
import { useAudienceAndCampaignOptions, useCampaignFilter } from "@/http/api-hooks";
import { CampaignType, CampaignTypeValuesInstallOnly } from "@/http/api-types";
import { useUrlParams } from "@/http/use-url-params";
import { campaignTypeLabels } from "@/lib/mappings";
import { formatDateToYYYYMMDD, getAudienceSelectOptions, getCampaignSelectOptions } from "@/lib/utils";
import { addDays } from "date-fns";
import { useState } from "react";
import SankeyChart from "./sankey-chart";
import StackedAreaChart from "./stacked-area-chart";

function CampaignFilter() {
  const { paramState, updateParam, updateDateRange, hasUrlParams } = useUrlParams({
    campaign: {
      name: "campaign",
      type: "string",
      defaultValue: "",
    },
    audience: {
      name: "audience",
      type: "string",
      defaultValue: "",
    },
    campaignType: {
      name: "campaign_type",
      type: "enum",
      validValues: CampaignTypeValuesInstallOnly as readonly string[],
      defaultValue: "all" as CampaignType,
    },
    fromDate: {
      name: "from",
      type: "date",
      defaultValue: addDays(new Date(), -1),
    },
    toDate: {
      name: "to",
      type: "date",
      defaultValue: new Date(),
    },
  });

  const currentDate = {
    from: paramState.fromDate,
    to: paramState.toDate,
  };

  const campaign = paramState.campaign;
  const audience = paramState.audience;
  const isReadyToFetch = hasUrlParams && currentDate?.from && currentDate?.to && campaign !== "";
  const [readyToFetch, setReadyToFetch] = useState<boolean>(isReadyToFetch);

  const { data: audienceCampaignData, isLoading: audienceOptionsLoading } = useAudienceAndCampaignOptions({
    campaign_type: paramState.campaignType as string,
  });

  const buttonDisabled = !campaign || !currentDate?.from || !currentDate?.to;

  const shouldFetch = !buttonDisabled && readyToFetch;
  const {
    data: campaignFilterData,
    isLoading: campaignFilterLoading,
    error: campaignFilterError,
  } = useCampaignFilter(
    {
      campaign_id: (campaign !== "" && campaign) || undefined,
      start_date: currentDate?.from && formatDateToYYYYMMDD(currentDate.from),
      end_date: currentDate?.to && formatDateToYYYYMMDD(currentDate.to),
    },
    shouldFetch,
  );

  const audienceOptions = getAudienceSelectOptions(audienceCampaignData);

  const onCampaignChange = (value: string) => {
    updateParam("campaign", value);
  };

  const onAudienceChange = (value: string) => {
    updateParam("campaign", "");
    updateParam("audience", value);
  };

  const onCampaignTypeChange = (value: string) => {
    updateParam("audience", "");
    updateParam("campaign", "");
    updateParam("campaignType", value);
  };

  const campaignOptions = getCampaignSelectOptions(audienceOptions, audience);

  const handleDateChange = (newDate: CalendarDates) => {
    updateDateRange(newDate, "fromDate", "toDate");

    updateParam("campaign", "");
  };

  const handleShowButtonClick = () => {
    updateDateRange(currentDate, "fromDate", "toDate");
    if (campaign) {
      updateParam("campaign", campaign);
    }
    setReadyToFetch(true);
  };

  const showAllAudiencesOption = paramState.campaignType === "ctv_install";

  return (
    <>
      <div className="flex flex-col gap-4 mb-8">
        <div>
          <ToggleGroup
            type="single"
            value={paramState.campaignType as string}
            onValueChange={onCampaignTypeChange}
            variant="outline"
          >
            <ToggleGroupItem value={"all"} className="min-w-[100px]">
              {campaignTypeLabels.all}
            </ToggleGroupItem>
            <ToggleGroupItem value={"install"} className="min-w-[100px]">
              {campaignTypeLabels.install}
            </ToggleGroupItem>
            <ToggleGroupItem value={"ctv_install"} className="min-w-[100px]">
              {campaignTypeLabels.ctv_install}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex justify-start space-x-8">
          <div className="grid grid-cols-[repeat(4,260px)] gap-4">
            <div>
              <Label className="font-semibold">Date</Label>
              <CalendarDateRangePicker className="w-full" date={currentDate} setDate={handleDateChange} />
            </div>
            <div>
              <Label className="font-semibold">Audience</Label>
              <Combobox
                value={audience}
                setValue={onAudienceChange}
                isLoading={audienceOptionsLoading}
                options={[
                  ...(showAllAudiencesOption ? [{ label: "All Audiences", value: "0" }] : []),
                  ...(audienceOptions
                    ? Object.keys(audienceOptions).map(audienceId => ({
                        label: audienceId + " - " + audienceOptions[audienceId].audience_name,
                        value: audienceId.toString(),
                      }))
                    : []),
                ]}
                disabled={false}
                buttonPlaceholder="Select audience"
                searchPlaceholder="Search audiences"
                notFoundMessage="No audiences found"
              />
            </div>
            <div>
              <Label className="font-semibold">
                Campaign <QuestionMarkTooltip content="Select a specific campaign or leave empty for all campaigns." />
              </Label>
              <Combobox
                value={campaign}
                setValue={onCampaignChange}
                isLoading={audienceOptionsLoading}
                disabled={false}
                options={
                  audienceOptions &&
                  campaignOptions.map(option => ({
                    label: option.id + " - " + option.name,
                    value: option.id.toString(),
                  }))
                }
                buttonPlaceholder="Select campaign"
                searchPlaceholder="Search campaigns"
                notFoundMessage="No campaigns found"
              />
            </div>
            <div className="flex items-end align-baseline">
              <Button onClick={handleShowButtonClick} disabled={buttonDisabled}>
                {campaignFilterLoading ? <LoadingSpinner /> : "Show"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Separator className="my-12" />
      <div className="grid grid-cols-12 gap-4">
        {campaignFilterLoading && <LoadingSpinner />}
        {campaignFilterError && (
          <div className="text-md">{`${campaignFilterError} Error loading campaign filter data `}</div>
        )}
        {campaignFilterData && (
          <>
            <div className="col-span-12 2xl:col-span-10 space-y-4">
              <StackedAreaChart rows={campaignFilterData.minute} />
            </div>
            <div className="col-span-12 2xl:col-span-10">
              <SankeyChart rows={campaignFilterData.all} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default CampaignFilter;
